import { useTranslation } from 'react-i18next';

import { itemByLanguage } from '@/utils/language';

import { Label } from '@/components/Filter/components/FilterCheckboxSection';
import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import TeamMemberCard from '@/components/PlanCards/TeamMemberCard';

import { UserPlans } from '@/_generated/api-clients/xp';
import { DataViewKey, useDataView } from '@/atoms/data-view';
import type { TeamMemberRow } from '@/routes/TrainingPlans/ManagePlans/View/PlanAssignees';

interface TeamMembersProps {
  currentFilters: string[];
  filterLocations: string[];
  isLearnUpon: boolean;
  isCompliance: boolean;
  isFoodSafety: boolean;
  onUnassign: (id: string, name: string) => void;
  onUpdateDueDate: (id: string, name: string) => void;
  plan?: UserPlans;
  planId: string;
  sort: string;
  statusLabels: Label;
  stepCount: number;
  teamMembers: TeamMemberRow[];
  dataviewKey: DataViewKey;
  isReportsRoute?: boolean;
  userCanMarkAsComplete?: boolean;
  userAllowedToUpdateTMDueDateOnPlan?: boolean;
}

const TeamMembers: React.FC<TeamMembersProps> = ({
  currentFilters,
  isLearnUpon,
  isCompliance,
  isFoodSafety,
  onUnassign,
  onUpdateDueDate,
  plan,
  planId,
  stepCount,
  teamMembers,
  dataviewKey,
  isReportsRoute,
  userCanMarkAsComplete,
  userAllowedToUpdateTMDueDateOnPlan,
}) => {
  const { t } = useTranslation();

  const [
    {
      display: { showing, total },
    },
    { showMore },
  ] = useDataView(dataviewKey);

  return (
    <div className="relative flex max-w-full gap-6 pt-1 pl-1">
      <div className="flex grow flex-col">
        <div className="relative flex max-w-full grow flex-col gap-4">
          {!!teamMembers.length && (
            <>
              {teamMembers.map(member => (
                <TeamMemberCard
                  {...member}
                  isReportsPlansView={isReportsRoute}
                  isReports={isReportsRoute}
                  isLearnUpon={isLearnUpon}
                  isCompliance={isCompliance}
                  isFoodSafety={isFoodSafety}
                  key={member.id}
                  name={member.simpleUserName || member.name || ''}
                  onUnassign={(id, name) => onUnassign(id, name)}
                  onUpdateDueDate={(id, name) => onUpdateDueDate(id, name)}
                  planId={planId}
                  planName={itemByLanguage(plan?.plan?.name)}
                  stepCount={stepCount}
                  userCanMarkAsComplete={userCanMarkAsComplete}
                  userAllowedToUpdateTMDueDateOnPlan={
                    userAllowedToUpdateTMDueDateOnPlan
                  }
                  complianceDueDateOffset={plan?.plan?.dueDateOffsetInDays}
                />
              ))}
              <LoadMorePaginator
                onClick={showMore}
                showing={showing}
                showingText={t('TrainingPlans.showingXOfYTeamMembers', {
                  showing: showing,
                  total: total,
                })}
                total={total}
              />
            </>
          )}
          {!teamMembers.length && !currentFilters.length && !isReportsRoute && (
            <p
              className="leading-snug"
              dangerouslySetInnerHTML={{
                __html: t('TrainingPlans.noTeamMembersAssignedManagePlans'),
              }}
            />
          )}
          {!teamMembers.length && !currentFilters.length && isReportsRoute && (
            <p
              className="leading-snug"
              dangerouslySetInnerHTML={{
                __html: t('TrainingPlans.noTeamMembersAssigned'),
              }}
            />
          )}
          {!!currentFilters.length && !total && (
            <p
              dangerouslySetInnerHTML={{
                __html: t('TrainingPlans.noTeamMembersResults'),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamMembers;
